// =====================================================================
//  PRESUPUESTO Y POSTURAS
//
//  Suma, para cada concesión marcada Alta o Media que siga en remate, el
//  mayor valor entre la postura definida y el mínimo legal.
//
//  Dos correcciones sobre el prototipo:
//
//  §5.2 — la garantía del art. 148 se calcula sobre los LOTES completos
//  que tocamos, no solo sobre las concesiones marcadas, y se muestra
//  aparte porque es caja adicional que no se descuenta del presupuesto.
//
//  §5.3 — fijar el presupuesto inserta una fila nueva en el historial en
//  vez de sobrescribir. Bajarlo toma efecto de inmediato, y queda registro
//  de con qué cifra se trabajaba en cada momento.
// =====================================================================

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useCausa,
  useConcesiones,
  useFijarPresupuesto,
  usePresupuesto,
  usePuntajes,
} from '../datos/consultas'
import { Alerta, Cargando, ErrorCarga, Tag, TituloCard, Vacio, useAvisar } from '../componentes/ui'
import { Pagina } from '../App'
import { fecha, fmt, num, plural } from '../dominio/formato'
import { garantiaPorLote } from '../dominio/calculo'
import { INTERES, type RolUsuario } from '../tipos/dominio'

export function Presupuesto({ rol }: { rol: RolUsuario }) {
  const concesiones = useConcesiones()
  const presupuesto = usePresupuesto()
  const puntajes = usePuntajes()
  const causa = useCausa()
  const fijar = useFijarPresupuesto()
  const avisar = useAvisar()
  const [, setParams] = useSearchParams()

  const [borrador, setBorrador] = useState('')
  const esMaster = rol === 'MASTER'

  if (concesiones.isError) return <ErrorCarga error={concesiones.error} />
  if (concesiones.isLoading || !concesiones.data) return <Cargando />

  const todas = concesiones.data
  const p = presupuesto.data
  const monto = p?.presupuesto === null || p?.presupuesto === undefined ? null : num(p.presupuesto)
  const comprometido = num(p?.comprometido)
  const garantia = garantiaPorLote(todas)
  const brecha = garantia.lotes - garantia.soloMarcadas

  const marcadas = todas
    .filter((c) => c.estado === 'EN_REMATE' && (c.interes === 'ALTA' || c.interes === 'MEDIA'))
    .sort((a, b) => num(b.compromiso) - num(a.compromiso))

  const puntajePor = new Map(puntajes.data?.map((x) => [x.concesion_id, x.puntaje]) ?? [])
  const pct = monto && monto > 0 ? Math.min(100, (comprometido / monto) * 100) : 0
  const excedido = monto !== null && comprometido > monto

  const guardarMonto = async () => {
    const v = Number(borrador)
    if (!v || v <= 0 || !causa.data) return
    try {
      await fijar.mutateAsync({
        causaId: causa.data.id,
        monto: v,
        glosa: `Fijado desde el portal el ${new Date().toLocaleDateString('es-CL')}`,
      })
      setBorrador('')
    } catch (e) {
      avisar(e)
    }
  }

  return (
    <>
      <Pagina
        titulo="Presupuesto y posturas"
        sub="Suma de las posturas de las concesiones marcadas con interés Alta o Media que siguen en remate. Cuando no hay postura definida se usa el mínimo legal, porque nunca se puede pagar menos que eso."
      />

      <div className="card" style={{ marginBottom: 16 }}>
        {monto === null ? (
          <Alerta tono="warn" estilo={{ marginTop: 0 }}>
            <b>Todavía no hay un presupuesto fijado.</b> Sin una cifra de referencia el criterio
            «calce con el presupuesto» toma su valor más bajo para las 39, así que el puntaje sirve
            para ordenar pero no para dimensionar.
          </Alerta>
        ) : null}

        {esMaster ? (
          <div style={{ display: 'flex', gap: 9, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ marginBottom: 0, maxWidth: 260 }}>
              <label>Presupuesto total disponible (CLP)</label>
              <input
                type="number"
                value={borrador}
                placeholder={monto !== null ? String(monto) : '15000000'}
                onChange={(e) => setBorrador(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void guardarMonto()}
              />
            </div>
            <button
              className="btn pri"
              disabled={fijar.isPending || !borrador}
              onClick={() => void guardarMonto()}
            >
              {fijar.isPending ? 'Guardando…' : 'Fijar presupuesto'}
            </button>
            {monto !== null ? (
              <span className="sub">
                Vigente: {fmt(monto)} desde el {fecha(p?.presupuesto_desde)}. Fijar uno nuevo no
                borra el anterior: queda el historial.
              </span>
            ) : null}
          </div>
        ) : (
          <div className="field" style={{ marginBottom: 0, maxWidth: 320 }}>
            <label>Presupuesto total disponible (CLP) — lo fija el máster</label>
            <input value={monto !== null ? fmt(monto) : 'sin fijar'} readOnly className="solo-lectura" />
          </div>
        )}

        <div
          style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, margin: '16px 0 6px' }}
        >
          <span>
            Comprometido <b>{fmt(comprometido)}</b>
          </span>
          <span className="sub">
            {monto !== null ? `Disponible ${fmt(Math.max(0, monto - comprometido))}` : '—'}
          </span>
        </div>
        <div className="bar" style={{ height: 12 }}>
          <i
            style={{
              width: `${pct}%`,
              background: excedido ? 'var(--bad)' : pct > 80 ? 'var(--warn)' : 'var(--ok)',
            }}
          />
        </div>

        {excedido ? (
          <Alerta tono="bad" estilo={{ margin: '16px 0 0' }}>
            <b>Excedido en {fmt(comprometido - monto!)}.</b> Baja el interés de las peor evaluadas o
            ajusta las posturas.
          </Alerta>
        ) : marcadas.length === 0 ? (
          <Alerta tono="info" estilo={{ margin: '16px 0 0' }}>
            Todavía no están marcadas concesiones con interés Alta o Media. Abre el listado y clasifica:
            solo esas dos entran al cálculo.
          </Alerta>
        ) : (
          <Alerta tono="ok" estilo={{ margin: '16px 0 0' }}>
            Dentro de presupuesto. Además de la postura hay que rendir <b>{fmt(garantia.lotes)}</b> en
            boletas de garantía y pagar las costas en proporción al precio (art. 150).
          </Alerta>
        )}
      </div>

      {/* --------- La garantía del art. 148, separada a propósito --------- */}
      <div className="card" style={{ marginBottom: 16 }}>
        <TituloCard>Garantía a rendir · art. 148</TituloCard>
        <p className="sub" style={{ marginBottom: 12 }}>
          Boleta de depósito a la orden del juzgado por el total adeudado del lote. <b>No</b> es lo
          mismo que la postura y <b>no</b> se descuenta del presupuesto: es caja que hay que tener
          disponible el día del remate.
        </p>
        <dl className="dl" style={{ gridTemplateColumns: '260px 1fr', margin: 0 }}>
          <dt>Sobre los lotes completos</dt>
          <dd>
            <b style={{ color: brecha > 0 ? 'var(--warn)' : undefined }}>{fmt(garantia.lotes)}</b>{' '}
            <span className="sub">
              — {plural(garantia.lotesTocados, 'lote', 'lotes')} con al menos una concesión marcada
            </span>
          </dd>
          <dt>Sumando solo lo marcado</dt>
          <dd>
            {fmt(garantia.soloMarcadas)}{' '}
            <span className="sub">— así lo calculaba el prototipo</span>
          </dd>
          {brecha > 0 ? (
            <>
              <dt style={{ color: 'var(--warn)' }}>Diferencia</dt>
              <dd>
                <b style={{ color: 'var(--warn)' }}>{fmt(brecha)}</b>{' '}
                <span className="sub">
                  — caja adicional por las concesiones que vienen amarradas al lote sin estar marcadas
                </span>
              </dd>
            </>
          ) : null}
        </dl>
        {brecha > 0 ? (
          <Alerta tono="warn" estilo={{ margin: '14px 0 0' }}>
            Si la mina que interesa viene amarrada a otras, hay que llevar la garantía de todas. Es
            el punto que conviene resolver antes de septiembre: revisa{' '}
            <a href="#/lotes">Lotes de subasta</a>.
          </Alerta>
        ) : null}
      </div>

      <div className="card">
        <TituloCard>Detalle por concesión marcada</TituloCard>
        <table>
          <thead>
            <tr>
              <th>Concesión</th>
              <th>Interés</th>
              <th className="num">Mínimo</th>
              <th className="num">Postura</th>
              <th>Lote</th>
              <th className="num">Puntaje</th>
            </tr>
          </thead>
          <tbody>
            {marcadas.length === 0 ? (
              <Vacio columnas={6}>Aún no hay concesiones marcadas con interés Alta o Media.</Vacio>
            ) : (
              marcadas.map((c) => (
                <tr key={c.id} onClick={() => setParams({ ficha: c.rol_nacional })}>
                  <td>
                    <div className="nm">{c.nombre}</div>
                    <div className="sub">{c.comuna}</div>
                  </td>
                  <td>
                    <Tag clase={INTERES[c.interes].c}>{INTERES[c.interes].t}</Tag>
                  </td>
                  <td className="num sub">{fmt(c.minimo_subasta)}</td>
                  <td className="num">
                    <b>{fmt(c.compromiso)}</b>
                    {!num(c.postura_max) ? <div className="sub">usa el mínimo</div> : null}
                  </td>
                  <td className="sub">
                    {c.lote ? (
                      <Tag clase={c.lote_confirmado ? 't-pagada' : 't-media'}>{c.lote}</Tag>
                    ) : (
                      'individual'
                    )}
                  </td>
                  <td className="num">{puntajePor.get(c.id) ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}
